export interface LoanMetrics {
  monthlyPayment: number
  totalPayment: number
  totalInterest: number
  monthlyRate: number
  termMonths: number
}

export function parseAnnualRate(value: string): number {
  const normalized = Number.parseFloat(value.replace('%', '').trim())
  return Number.isFinite(normalized) ? normalized / 100 : 0
}

export function calculateLoanMetrics(
  principal: number,
  annualRate: number,
  tenureYears: number,
  processingFeeRate: number = 0
): LoanMetrics {
  const safePrincipal = Math.max(0, principal)
  const safeRate = Math.max(0, annualRate)
  const termMonths = Math.max(0, Math.round(tenureYears * 12))
  const safeFeeRate = Math.max(0, processingFeeRate)

  if (safePrincipal === 0 || termMonths === 0) {
    return {
      monthlyPayment: 0,
      totalPayment: 0,
      totalInterest: 0,
      monthlyRate: safeRate / 12,
      termMonths,
    }
  }

  // Flat (Simple) Interest calculation: Principal * APR * Years
  const flatInterest = safePrincipal * safeRate * tenureYears
  
  // Processing Fee calculation: Principal * Fee Rate
  const feeAmount = safePrincipal * safeFeeRate
  
  // Total Payment = Principal + Interest + Fee
  const exactTotalPayment = safePrincipal + flatInterest + feeAmount

  // Monthly Payment = Total Payment / Term Months
  const rawMonthlyPayment = exactTotalPayment / termMonths

  // Banking standard: Round monthly payment to 2 decimal places to ensure totals match exactly
  const monthlyPayment = Math.round(rawMonthlyPayment * 100) / 100
  const totalPayment = monthlyPayment * termMonths

  return {
    monthlyPayment,
    totalPayment,
    totalInterest: Math.round((totalPayment - safePrincipal) * 100) / 100,
    monthlyRate: safeRate / 12, // Keeps interface compatibility
    termMonths,
  }
}
